import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { useCreateComplianceReport } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { format, subMonths, startOfQuarter, endOfQuarter, subQuarters } from "date-fns";

interface CreateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateReportDialog({ open, onOpenChange }: CreateReportDialogProps) {
  const { user } = useAuth();
  const isOwner = user?.roles?.includes("owner") ?? false;

  const now = new Date();
  const lastQuarter = subQuarters(now, 1);
  const defaultStart = format(startOfQuarter(lastQuarter), "yyyy-MM-dd");
  const defaultEnd = format(endOfQuarter(lastQuarter), "yyyy-MM-dd");

  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [includePii, setIncludePii] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createReport = useCreateComplianceReport();

  function handleSubmit() {
    setError(null);

    if (!periodStart || !periodEnd) {
      setError("Both start and end dates are required");
      return;
    }

    if (periodStart >= periodEnd) {
      setError("Start date must be before end date");
      return;
    }

    createReport.mutate(
      {
        period_start: periodStart,
        period_end: periodEnd,
        include_pii: includePii,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setPeriodStart(defaultStart);
          setPeriodEnd(defaultEnd);
          setIncludePii(false);
        },
        onError: (err) => {
          setError(err.message || "Failed to create report");
        },
      },
    );
  }

  function setQuarterPreset(quarters: number) {
    const target = subQuarters(now, quarters);
    setPeriodStart(format(startOfQuarter(target), "yyyy-MM-dd"));
    setPeriodEnd(format(endOfQuarter(target), "yyyy-MM-dd"));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Compliance Report</DialogTitle>
          <DialogDescription>
            Generate a SOC 2 Type II compliance report for the selected period.
            This may take a few minutes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Quick presets */}
          <div>
            <Label className="text-sm font-medium">Quick Select</Label>
            <div className="mt-1.5 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setQuarterPreset(1)}
              >
                Last Quarter
              </Button>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setQuarterPreset(2)}
              >
                2 Quarters Ago
              </Button>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setPeriodStart(format(subMonths(now, 1), "yyyy-MM-dd"));
                  setPeriodEnd(format(now, "yyyy-MM-dd"));
                }}
              >
                Last 30 Days
              </Button>
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="period-start">Start Date</Label>
              <input
                id="period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <Label htmlFor="period-end">End Date</Label>
              <input
                id="period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* PII toggle */}
          {isOwner && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="include-pii" className="font-medium">
                  Include PII
                </Label>
                <p className="text-xs text-muted-foreground">
                  Include email addresses and IP addresses in the report
                </p>
              </div>
              <Switch
                id="include-pii"
                checked={includePii}
                onCheckedChange={setIncludePii}
              />
            </div>
          )}

          {error && (
            <div id="report-form-error" role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createReport.isPending} aria-describedby={error ? "report-form-error" : undefined}>
            {createReport.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Generate Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
